import { AppBar, Toolbar, Button, Box, Menu, MenuItem } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useContext, useState, useRef, useEffect } from 'react';
import AuthContext from './AuthContext';
import logo from './DO_Logo.png';

const navbarbuttonspacing = 100;
// const authButtonGap = 16;

export default function NavBar(props) {
  const { token, logout } = useContext(AuthContext);
  const isLoggedIn = Boolean(token);
  const [servicesAnchorEl, setServicesAnchorEl] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const servicesOpen = Boolean(servicesAnchorEl);
  const servicesButtonRef = useRef(null);
  const menuPaperRef = useRef(null);
  const menuItemRefs = useRef([]);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isMenuHovered, setIsMenuHovered] = useState(false);
  const hoverCloseTimerRef = useRef(null);
  const navigate = useNavigate();

  const handleServicesEnter = (event) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
    setIsButtonHovered(true);
    setServicesAnchorEl(event.currentTarget);
    
  };

  const handleServicesLeave = (event) => {
    // mark button as not hovered; use the shared effect to close if needed
    setIsButtonHovered(false);
    
  };

  const handleMenuEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
    setIsMenuHovered(true);
    
  };

  const handleMenuLeave = (event) => {
    setIsMenuHovered(false);
    
  };

  // Keep the menu open while either the services button or the menu is hovered.
  useEffect(() => {
    // Clear any existing close timer
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }

    if (isButtonHovered || isMenuHovered) {
      // ensure menu is open (anchor might be missing if opened via keyboard)
      if (!servicesOpen && servicesButtonRef.current) {
        setServicesAnchorEl(servicesButtonRef.current);
      }
      
      return;
    }

    // start a delayed close when neither is hovered
    // start a delayed close when neither is hovered
    // number at end of this block is the delay in milliseconds before the menu closes after mouse leaves
    
    hoverCloseTimerRef.current = setTimeout(() => {
      setServicesAnchorEl(null);
      hoverCloseTimerRef.current = null;
    }, 75);

    return () => {
      if (hoverCloseTimerRef.current) {
        clearTimeout(hoverCloseTimerRef.current);
        hoverCloseTimerRef.current = null;
      }
    };
  }, [isButtonHovered, isMenuHovered, servicesOpen]);

  // When the menu opens, focus the first menu item after a short delay so
  // that assistive tech sees focus inside the visible menu (avoid focusing
  // too early while aria-hidden may still be toggled).
  useEffect(() => {
    if (servicesOpen) {
      const t = setTimeout(() => {
        try {
          menuItemRefs.current[0]?.focus();
        } catch (e) {}
      }, 600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [servicesOpen]);

  // Track pointer position globally while the menu is open. If the pointer
  // is within an expanded region around either the Services button or the
  // menu panel, keep hover state true so the menu won't close while the
  // user moves the mouse between them. This is a robust fallback for
  // portal-related mouseenter/leave inconsistencies.
  useEffect(() => {
    if (!servicesOpen) return undefined;

    let mounted = true;
    const padding = 12; // px expansion around elements to consider "nearby"

    const onPointerMove = (ev) => {
      if (!mounted) return;
      const x = ev.clientX;
      const y = ev.clientY;

      const btnRect = servicesButtonRef.current?.getBoundingClientRect();
      const menuRect = menuPaperRef.current?.getBoundingClientRect();

      const inside = (rect) => {
        if (!rect) return false;
        return x >= (rect.left - padding) && x <= (rect.right + padding) && y >= (rect.top - padding) && y <= (rect.bottom + padding);
      };

      const nearBtn = inside(btnRect);
      const nearMenu = inside(menuRect);

      // Update hover state based on proximity. These updates are intentionally
      // coarse (may run many times during mousemove) but keep the UI stable.
      setIsButtonHovered(nearBtn);
      setIsMenuHovered(nearMenu);
    };

    document.addEventListener('mousemove', onPointerMove, { passive: true });
    // also track touchstart for touch-based interactions
    document.addEventListener('touchstart', onPointerMove, { passive: true });

    return () => {
      mounted = false;
      document.removeEventListener('mousemove', onPointerMove);
      document.removeEventListener('touchstart', onPointerMove);
    };
  }, [servicesOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
  <AppBar position="fixed" sx={{ left: 0, right: 'auto', width: '100%', minWidth: '1300px', background: '#000',
   boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 1300, borderBottom: '2px solid #41FBFF' }}>
      <Toolbar disableGutters
      sx={{ width: '100%', minWidth: '1300px', boxSizing: 'border-box', display: 'flex', alignItems: 'center', position: 'relative'}}>
        <Box sx={{ display: 'flex', alignItems: 'center'}}>
          <img src={logo} alt="Logo" style={{ height: '65px', width: '110px' }} />
        </Box>
        <Box sx={{position: 'absolute', left: 'max(750px, 50vw)', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center',
         justifyContent: 'center', gap: `${navbarbuttonspacing}px`, whiteSpace: 'nowrap' }}>
          <Button component={RouterLink} to={isLoggedIn ? '/dashboard' : '/'} sx={{ color: '#a4fdffff' }}>
            Home
          </Button>
          {!isLoggedIn && (
            <>
              <Button ref={servicesButtonRef} sx={{ color: '#a4fdffff' }} onMouseEnter={handleServicesEnter} onMouseLeave={handleServicesLeave}>              
                Services
              </Button>
              <Menu anchorEl={servicesAnchorEl} open={servicesOpen} onClose={() => setServicesAnchorEl(null)} keepMounted
                MenuListProps={{
                  onMouseEnter: handleMenuEnter,
                  onMouseLeave: handleMenuLeave,
                }}
                sx={{ '& .MuiPaper-root': { backgroundColor: '#000', border: '1px solid #41FBFF', color: '#a4fdffff' }}}
                PaperProps={{ ref: menuPaperRef }}
              >
                <MenuItem ref={(el) => (menuItemRefs.current[0] = el)} component={RouterLink} to="/adval_taxes" onClick={handleMenuLeave}
                  sx={{ color: '#a4fdffff', '&:hover': { backgroundColor: '#333' } }}
                >
                  Tax & Economic Evaluation Tool
                </MenuItem>
                <MenuItem
                  ref={(el) => (menuItemRefs.current[0] = el)}
                  component={RouterLink}
                  to="/digitization"
                  onClick={handleMenuLeave}
                  sx={{ color: '#a4fdffff', '&:hover': { backgroundColor: '#333' } }}
                >
                  Log Digitization
                </MenuItem>
                <MenuItem ref={(el) => (menuItemRefs.current[1] = el)} component={RouterLink} to="/digitization#geotech-services"
                  onClick={handleMenuLeave} sx={{ color: '#a4fdffff', '&:hover': { backgroundColor: '#333' } }}>
                  Geotech Services
                </MenuItem>
                <MenuItem ref={(el) => (menuItemRefs.current[2] = el)} component={RouterLink} to="/digitization#custom-solutions"
                  onClick={handleMenuLeave} sx={{ color: '#a4fdffff', '&:hover': { backgroundColor: '#333' } }}>
                  Custom Solutions
                </MenuItem>
              </Menu>
              <Button component={RouterLink} to="/team" sx={{ color: '#a4fdffff' }}>
                Digital Operating Team
              </Button>
            </>
          )}
          {isLoggedIn ? (
              <Button sx={{ color: '#a4fdffff' }} onClick={handleLogout}>
                Logout
              </Button>
            ) : (
              <>
                <Button sx={{ color: '#a4fdffff' }} component={RouterLink} to="/login">
                  Login
                </Button>
                <Button sx={{ color: '#a4fdffff' }} component={RouterLink} to="/signup">
                  Sign Up
                </Button>
              </>
            )}
        </Box>
        {/* <Box sx={{ position: 'absolute', display: 'flex', alignItems: 'center', gap: `50px`,
          right: 'min(125px, calc(100% - 1275px))' }}>
            {isLoggedIn ? (
              <Button sx={{ color: '#a4fdffff' }} onClick={handleLogout}>
                Logout
              </Button>
            ) : (
              <>
                <Button sx={{ color: '#a4fdffff' }} component={RouterLink} to="/login">
                  Login
                </Button>
                <Button sx={{ color: '#a4fdffff' }} component={RouterLink} to="/signup">
                  Sign Up
                </Button>
              </>
            )}
          </Box> */}
      </Toolbar>
    </AppBar>
  );
}
